import { OVERLAPS, SESSIONS } from '@/mocks/marketCalendar';
import { formatCountdown, minutesUntil, minutesToLabel, sessionStatus } from '../calculations';

export default function MarketClock({
  nowUtcMinutes,
  toDisplay,
}: {
  nowUtcMinutes: number;
  toDisplay: (utc: number) => number;
}) {
  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
            Đồng hồ phiên giao dịch
          </h2>
          <p className="mt-1.5 text-sm text-slate-500">Trạng thái mở cửa của bốn phiên giao dịch chính trên thế giới.</p>
        </div>
      </div>

      {/* 24h segmented bar */}
      <div className="mt-6">
        <div className="relative h-12 w-full overflow-hidden rounded-xl bg-slate-100">
          {SESSIONS.map((s) => {
            const start = toDisplay(s.startUtc);
            const end = toDisplay(s.endUtc);
            const segments =
              start < end
                ? [{ left: start, width: end - start }]
                : [
                    { left: start, width: 1440 - start },
                    { left: 0, width: end },
                  ];
            const open = sessionStatus(s, nowUtcMinutes) === 'open';
            return segments.map((seg, i) => (
              <span
                key={`${s.id}-${i}`}
                className="absolute top-0 flex h-full items-center justify-center px-2 text-[11px] font-semibold text-white"
                style={{
                  left: `${(seg.left / 1440) * 100}%`,
                  width: `${(seg.width / 1440) * 100}%`,
                  backgroundColor: s.color,
                  opacity: open ? 1 : 0.35,
                }}
              >
                {seg.width > 110 ? s.name : ''}
              </span>
            ));
          })}
          {/* NOW marker */}
          <div
            className="absolute top-0 h-full"
            style={{ left: `${(nowUtcMinutes / 1440) * 100}%` }}
          >
            <div className="h-full w-0.5 bg-slate-900" />
          </div>
        </div>
        <div className="mt-2 flex justify-between font-mono text-[10px] text-slate-400">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>24:00</span>
        </div>
      </div>

      {/* session cards */}
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {SESSIONS.map((s) => {
          const status = sessionStatus(s, nowUtcMinutes);
          const start = toDisplay(s.startUtc);
          const end = toDisplay(s.endUtc);
          let remaining = 0;
          if (status === 'open') remaining = minutesUntil(end, nowUtcMinutes) * 60000;
          else if (status === 'starts-in') remaining = minutesUntil(start, nowUtcMinutes) * 60000;

          return (
            <div key={s.id} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4">
              <div className="flex items-center gap-2.5">
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${s.color}1a`, color: s.color }}
                >
                  <i className={s.icon} />
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-800">{s.name}</p>
                  <p className="font-mono text-[11px] text-slate-400">
                    {minutesToLabel(start)}–{minutesToLabel(end)}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                    status === 'open'
                      ? 'bg-emerald-50 text-emerald-600'
                      : status === 'starts-in'
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {status === 'open' ? 'OPEN' : status === 'starts-in' ? 'STARTS IN' : 'CLOSED'}
                </span>
                {status === 'open' || status === 'starts-in' ? (
                  <span className="font-mono text-xs font-semibold tabular-nums text-slate-700">
                    {formatCountdown(remaining)}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Overlap */}
      <div className="mt-6 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-5">
        <h3 className="text-sm font-bold text-slate-800">Thời điểm các phiên giao dịch chồng lấn</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {OVERLAPS.map((o) => {
            const active = nowUtcMinutes >= o.startUtc && nowUtcMinutes < o.endUtc;
            const start = toDisplay(o.startUtc);
            const end = toDisplay(o.endUtc);
            return (
              <div
                key={o.id}
                className={`flex items-center justify-between rounded-xl px-4 py-3 ${
                  active ? 'bg-violet-50 ring-1 ring-violet-200' : 'bg-white ring-1 ring-slate-200'
                }`}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-800">{o.name}</p>
                  <p className="font-mono text-[11px] text-slate-400">
                    {minutesToLabel(start)}–{minutesToLabel(end)}
                  </p>
                </div>
                {active && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                    Đang giao thoa
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}